import { useAuth } from '@state/auth';

/**
 * Consumes a session handed over from frontend-v2 in the URL hash.
 *
 * This is the mirror of `buildV2RedirectUrl`, which sends the session the other
 * way when the old dashboard opens v2. It exists because localStorage is
 * per-origin: v2 signs the user in at its own origin, so without a transfer the
 * old frontend sees no session and bounces a just-authenticated user to its own
 * login screen.
 *
 * Same envelope as the outbound direction — AES-GCM under
 * `VITE_REDIRECT_ENCRYPTION_KEY`, base64url, `#token=…` — with the plaintext
 * `#accessToken=…` hash as the no-key fallback.
 *
 * Never throws: a malformed or stale hash leaves the app exactly as it was, so
 * the worst case is the login screen the user would have seen anyway.
 */

const STORAGE_KEY = 'retraced:context';

interface HandoffPayload {
  accessToken?: string | null;
  sessionToken?: string | null;
  refreshToken?: string | null;
  impersonateId?: string | null;
  companyId?: string | null;
}

export async function seedSessionFromHash(): Promise<void> {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const encrypted = params.get('token');
    const payload = encrypted
      ? await decryptToken(encrypted)
      : plaintextPayload(params);

    if (!payload?.accessToken) return;

    writeSession(payload);
    stripHash();

    // The store already hydrated from an empty localStorage during module
    // evaluation (imports run before this function does), so it has to be told
    // to read again rather than being left with its initial null session.
    await useAuth.persist?.rehydrate?.();
  } catch {
    // Ignore — see the "never throws" note above.
  }
}

async function decryptToken(encoded: string): Promise<HandoffPayload | null> {
  const keyStr = import.meta.env.VITE_REDIRECT_ENCRYPTION_KEY;
  if (!keyStr) return null;

  const raw = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(keyStr),
  );
  const key = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [
    'decrypt',
  ]);

  const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const combined = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: combined.slice(0, 12) },
    key,
    combined.slice(12),
  );

  return JSON.parse(new TextDecoder().decode(decrypted)) as HandoffPayload;
}

function plaintextPayload(params: URLSearchParams): HandoffPayload {
  return {
    accessToken: params.get('accessToken'),
    sessionToken: params.get('sessionToken'),
    refreshToken: params.get('refreshToken'),
    impersonateId: params.get('impersonateId'),
    companyId: params.get('companyId'),
  };
}

/**
 * The refresh token already stored at this origin, if any.
 *
 * `writeSession` replaces the whole record, so a handoff that carries no
 * refresh token would otherwise erase a perfectly good one and leave this
 * origin unable to renew — a session that had been lasting up to the refresh
 * token's 30 days suddenly capped at the access token's 24h. Keeping the
 * stored value is always safe: both origins are issued the same token by the
 * same login.
 */
function storedRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { state } = JSON.parse(raw) as {
      state?: { refreshToken?: string | null };
    };
    return state?.refreshToken ?? null;
  } catch {
    return null;
  }
}

/** Writes the shape the `useAuth` persist middleware expects to rehydrate. */
function writeSession(payload: HandoffPayload): void {
  const refreshToken = payload.refreshToken ?? storedRefreshToken();
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      state: {
        accessToken: payload.accessToken ?? null,
        sessionToken: payload.sessionToken ?? null,
        refreshToken,
        impersonateId: payload.impersonateId ?? null,
        companyId: payload.companyId ?? null,
        sessionStartedAt: Date.now(),
        isSessionExpired: false,
        mePreferencesUpdated: false,
      },
      version: 0,
    }),
  );
}

/** Keeps the tokens out of the address bar, history, and any shared link. */
function stripHash(): void {
  history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
}
